import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { getCategoryName } from '../utils/adminFields';
import resolveImageUrl from '../utils/resolveImageUrl';
import {
  canUseGooglePlacesAutocomplete,
  getGooglePlacePredictions,
  resetGooglePlaceSessionToken,
} from '../utils/googlePlacesAutocomplete';
import useAnimeSvg from '../hooks/useAnimeSvg';
import useAnimeReveal from '../hooks/useAnimeReveal';
import useAnimePageLoad from '../hooks/useAnimePageLoad';
import heroImage from '../assets/AppImages/home_page/royal-car-rental-group-hero.jpg';
import ceoImage from '../assets/AppImages/home_page/ceo1.jpg';
import visionImage from '../assets/AppImages/home_page/our-vision.jpg';
import knowHowImage from '../assets/AppImages/home_page/know-how.jpg';
import fleetImage from '../assets/AppImages/products/cars-exec.jpg';
import carsBlack from '../assets/AppImages/car-logos/Mercedes Benz E300 Black 2017.jpg';
import executiveSuvImage from '../assets/AppImages/car-logos/Range Rover Vogue Gold.jpg';
import twinCabImage from '../assets/AppImages/car-logos/Isuzu D-Max White 2024.webp';
import mapMain from '../assets/AppImages/map/map-main.png';
import benzLogo from '../assets/AppImages/car-logos/benz.png';
import fordLogo from '../assets/AppImages/car-logos/ford.png';
import hondaLogo from '../assets/AppImages/car-logos/honda.png';
import isuzuLogo from '../assets/AppImages/car-logos/isuzu.png';
import landRoverLogo from '../assets/AppImages/car-logos/LandRover.svg.png';
import mazdaLogo from '../assets/AppImages/car-logos/mazda.png';
import nissanLogo from '../assets/AppImages/car-logos/nissan.png';
import rangeRoverLogo from '../assets/AppImages/car-logos/rangeRover.png';
import toyotaLogo from '../assets/AppImages/car-logos/toyota.png';

const locationOptions = [
  'Harare CBD',
  'Robert Gabriel Mugabe International Airport',
  'Waterfalls, Harare',
  'Borrowdale, Harare',
  'Avondale, Harare',
  'Belvedere, Harare',
  'Eastlea, Harare',
  'Greendale, Harare',
  'Highlands, Harare',
  'Mount Pleasant, Harare',
  'Newlands, Harare',
  'Samora Machel Avenue, Harare',
  'Westgate, Harare',
  'Msasa, Harare',
  'Joina City, Harare',
  'Meikles Hotel, Harare',
  'Sam Levy Village, Borrowdale',
  'Hatfield, Harare',
  'Chitungwiza',
  'Ruwa',
  'Victoria Falls',
  'Bulawayo',
];

const brandLogos = [
  { src: landRoverLogo, alt: 'Land Rover' },
  { src: isuzuLogo, alt: 'Isuzu' },
  { src: hondaLogo, alt: 'Honda' },
  { src: fordLogo, alt: 'Ford' },
  { src: benzLogo, alt: 'Mercedes Benz' },
  { src: toyotaLogo, alt: 'Toyota' },
  { src: rangeRoverLogo, alt: 'Range Rover' },
  { src: nissanLogo, alt: 'Nissan' },
  { src: mazdaLogo, alt: 'Mazda' },
];

const sectionTabs = [
  { id: 'home-fleet', number: '01', title: 'Our Fleet' },
  { id: 'home-values', number: '02', title: 'Our Values' },
  { id: 'home-vision', number: '03', title: 'Our Vision' },
  { id: 'home-know-how', number: '04', title: 'Our Know-How' },
  { id: 'home-locations', number: '05', title: 'Our Locations' },
];

const fallbackFleetCards = [
  {
    id: 1,
    type: 'category',
    title: 'Hatchback',
    description: 'Compact city vehicles and efficient daily hire options.',
    image: '/assets/fleet/honda-fit-hybrid-silver.png',
    imageAlt: 'Honda Fit Hybrid Silver — Hatchback',
    price: 60,
    badge: 'Premium',
    details: ['DeRoyalty', 'Premium', 'Reliable'],
  },
  {
    id: 2,
    type: 'category',
    title: 'Sedan',
    description: 'Comfortable sedan hire options for daily and business travel.',
    image: '/assets/fleet/toyota-axio.png',
    imageAlt: 'Toyota Axio — Sedan',
    price: 60,
    badge: 'Business',
    details: ['DeRoyalty', 'Premium', 'Reliable'],
  },
  {
    id: 3,
    type: 'category',
    title: 'SUV',
    description: 'Spacious sport utility vehicles for families, airport transfers, and long-distance travel.',
    image: '/assets/fleet/fortuner-hilux-d4d-white.png',
    imageAlt: 'Toyota Fortuner Hilux D4D White/Silver — SUV',
    price: 120,
    badge: 'All-terrain',
    details: ['DeRoyalty', 'Premium', 'Reliable'],
  },
  {
    id: 4,
    type: 'category',
    title: 'Executive Sedan',
    description: 'Premium executive sedans for formal business movement.',
    image: '/assets/fleet/mercedes-e300-navy-blue.png',
    imageAlt: 'Mercedes Benz E300 Navy Blue — Executive Sedan',
    price: 200,
    badge: 'Luxury',
    details: ['DeRoyalty', 'Premium', 'Reliable'],
  },
];

function normalizeFleetCategory(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');
}

function getProductCategoryName(product) {
  return product?.categoryName || product?.category?.categoryName || product?.category?.name || '';
}

function getProductCategoryId(product) {
  return product?.categoryId ?? product?.category?.id ?? null;
}

function getProductFleetImage(product) {
  const image = [
    product?.imageURL,
    product?.imageUrl,
    product?.image_url,
    ...(Array.isArray(product?.carousel_imgs) ? product.carousel_imgs : []),
  ].find((value) => typeof value === 'string' && value.trim());

  return image ? resolveImageUrl(image, '') : '';
}

function formatHomeFleetDailyRate(value) {
  const price = Number(value);
  if (!Number.isFinite(price) || price <= 0) return null;

  return `US$${price.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}/day`;
}

function productBelongsToCategory(product, category) {
  const categoryId = category?.id ?? null;
  const productCategoryId = getProductCategoryId(product);

  if (categoryId !== null && productCategoryId !== null) {
    return String(productCategoryId) === String(categoryId);
  }

  const categoryName = normalizeFleetCategory(getCategoryName(category));
  const productCategoryName = normalizeFleetCategory(getProductCategoryName(product));
  return Boolean(categoryName && productCategoryName && categoryName === productCategoryName);
}

function getRepresentativeFleetProduct(category, products) {
  const candidates = (Array.isArray(products) ? products : [])
    .filter((product) => productBelongsToCategory(product, category))
    .filter((product) => String(product?.name || '').trim() && getProductFleetImage(product));

  return candidates.find(
    (product) => normalizeFleetCategory(product?.bookingStatus) === 'available'
  ) || candidates[0] || null;
}

export function buildHomeFleetCards(categories, products) {
  if (!Array.isArray(categories) || !categories.length) return fallbackFleetCards;

  const badges = ['Premium', 'Business', 'All-terrain', 'Luxury', 'Executive', 'Reliable'];

  return categories
    .map((category, index) => {
      const representativeProduct = getRepresentativeFleetProduct(category, products);
      const title = getCategoryName(category);
      if (!representativeProduct || !title) return null;

      return {
        id: category.id,
        type: 'category',
        title,
        description: category.description || 'Explore this DeRoyalty fleet category.',
        image: getProductFleetImage(representativeProduct),
        imageAlt: `${representativeProduct.name} — ${title}`,
        price: Number(representativeProduct.price),
        badge: badges[index % badges.length],
        details: ['DeRoyalty', 'Premium', 'Reliable'],
      };
    })
    .filter(Boolean)
    .slice(0, 4);
}

const featuredFleetSlides = [
  {
    image: fleetImage,
    eyebrow: 'Premium fleet',
    title: 'Ready for every journey',
    alt: 'DeRoyalty premium fleet',
  },
  {
    image: carsBlack,
    eyebrow: 'Executive sedan',
    title: 'Executive comfort',
    alt: 'Mercedes Benz E300 Black executive sedan',
  },
  {
    image: executiveSuvImage,
    eyebrow: 'Luxury SUV',
    title: 'Flagship presence',
    alt: 'Range Rover Vogue Gold luxury SUV',
  },
  {
    image: twinCabImage,
    eyebrow: 'Twin-cab strength',
    title: 'Built for mixed routes',
    alt: 'Isuzu D-Max White twin cab',
  },
];

const bookingBenefits = [
  { icon: 'bi-shield-check', label: 'Best Price Guarantee' },
  { icon: 'bi-arrow-repeat', label: 'Flexible Booking' },
  { icon: 'bi-headset', label: '24/7 Support' },
];

const trustBenefits = [
  {
    icon: 'bi-headset',
    title: '24/7 Support',
    text: "We're here for you anytime, anywhere.",
  },
  {
    icon: 'bi-gem',
    title: 'Premium Fleet',
    text: 'Latest models. Luxury comfort. Always maintained.',
  },
  {
    icon: 'bi-shield-check',
    title: 'Verified Service',
    text: 'Trusted by happy customers across Africa.',
  },
  {
    icon: 'bi-star',
    title: 'Best Price Guarantee',
    text: 'Great value with no hidden fees.',
  },
];

const fleetBenefits = [
  { icon: 'bi-shield-check', title: 'Well Maintained', text: 'Quality assured' },
  { icon: 'bi-car-front', title: 'Wide Selection', text: 'Premium vehicles' },
  { icon: 'bi-gem', title: 'Best Experience', text: 'Luxury guaranteed' },
];

const valueHighlights = [
  { icon: 'bi-gem', title: 'Premium Fleet', text: 'Quality assured vehicles' },
  { icon: 'bi-car-front', title: 'Wide Selection', text: 'Options for every journey' },
  { icon: 'bi-award', title: 'Best Experience', text: 'Service built around clients' },
];

const knowHowCards = [
  { icon: 'bi-award', title: 'Experience', subtitle: '3 years operating', text: 'Focused expertise that drives every journey.' },
  { icon: 'bi-globe2', title: 'Global Standards', subtitle: 'Partnered with world-class companies', text: 'International quality you can rely on.' },
  { icon: 'bi-patch-check', title: 'Trusted Service', subtitle: 'Reliability, safety & client satisfaction', text: 'Committed to delivering excellence, every time.' },
];

const worldStats = [
  { icon: 'bi-clock-history', value: '3', label: 'Years Operating', text: 'Three years of delivering premium mobility.' },
  { icon: 'bi-globe2', value: '6', label: 'Countries', text: 'Proudly serving customers across regions.' },
  { icon: 'bi-buildings', value: '7', label: 'Branch Offices', text: 'Convenient locations, exceptional service.' },
];

const trustProofStats = [
  { icon: 'bi-clock-history', value: '3', label: '3 years operating' },
  { icon: 'bi-car-front', value: '24+', label: 'fleet vehicles and categories' },
  { icon: 'bi-buildings', value: '7', label: '7 branch offices' },
  { icon: 'bi-headset', value: '24/7', label: '24/7 client support' },
];

const whyDeroyaltyCards = [
  {
    icon: 'bi-person-check',
    title: 'Verified booking workflow',
    text: 'Verified accounts, clear handover details, and accountable support protect every rental request.',
  },
  {
    icon: 'bi-tools',
    title: 'Prepared premium fleet',
    text: 'Vehicles are selected, presented and maintained around comfort, reliability and route confidence.',
  },
  {
    icon: 'bi-geo-alt',
    title: 'Regional service reach',
    text: 'Branch and ambassador support helps clients move across business, family and airport journeys.',
  },
];

const clientTestimonials = [
  {
    quote: 'The vehicle was clean, ready on time, and the team kept the whole trip simple.',
    name: 'Executive rental client',
  },
  {
    quote: 'DeRoyalty handled the booking clearly from pickup to return, with support whenever we needed it.',
    name: 'Regional travel client',
  },
];

const locationRegions = [
  { label: 'Southern Africa', text: 'Zimbabwe · South Africa · Angola' },
  { label: 'Middle East', text: 'United Arab Emirates' },
  { label: 'Australia', text: 'Melbourne' },
];

function formatDate(date) {
  return date.toISOString().split('T')[0];
}

function normalizeBookingText(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');
}

function tokenizeBookingText(value) {
  return normalizeBookingText(value).split(' ').filter((token) => token.length > 1);
}

function productMatchesKeyword(product, keyword) {
  const tokens = tokenizeBookingText(keyword);
  if (!tokens.length) return true;
  const searchable = normalizeBookingText([
    product?.name,
    product?.categoryName,
    product?.category?.categoryName,
    product?.transmission,
  ].filter(Boolean).join(' '));
  return tokens.every((token) => searchable.includes(token));
}

function getGooglePlaceLabel(details, fallback) {
  const displayName = String(details?.displayName || '').trim();
  const formattedAddress = String(details?.formattedAddress || '').trim();
  const fallbackText = String(fallback || '').trim();

  if (displayName && formattedAddress) {
    const normalizedName = normalizeBookingText(displayName);
    const normalizedAddress = normalizeBookingText(formattedAddress);
    if (normalizedAddress.startsWith(normalizedName) || normalizedAddress.includes(normalizedName)) {
      return formattedAddress;
    }
    return `${displayName}, ${formattedAddress}`;
  }

  return formattedAddress || displayName || fallbackText;
}

function isValidTimeValue(value) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ''));
}

function getFilteredLocationOptions(query) {
  const tokens = tokenizeBookingText(query);
  if (!tokens.length) return locationOptions.slice(0, 8);

  return locationOptions
    .filter((location) => {
      const searchable = normalizeBookingText(location);
      return tokens.every((token) => searchable.includes(token));
    })
    .slice(0, 8);
}

function HomeView({ categories, products, userRole }) {
  const navigate = useNavigate();
  const normalizedUserRole = userRole?.toLowerCase();
  const heroBookHref = normalizedUserRole === 'admin'
    ? '/admin/bookings'
    : normalizedUserRole
      ? '/dashboard/fleet'
      : '/auth?mode=login';
  const pickupDateRef = useRef(null);
  const dropoffDateRef = useRef(null);
  const pickupTimeRef = useRef(null);
  const dropoffTimeRef = useRef(null);
  const googlePlaceSessionTokenRef = useRef(null);
  const googlePlaceRequestIdRef = useRef(0);
  const rootRef = useAnimePageLoad({ selector: '[data-anime-enter]' });
  useAnimeReveal({ selector: '[data-anime]', rootRef });
  useAnimeSvg({ rootRef });
  const tomorrow = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return formatDate(date);
  }, []);

  const [pickupDate, setPickupDate] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [pickupTime, setPickupTime] = useState('10:00');
  const [dropoffTime, setDropoffTime] = useState('10:00');
  const [pickupLocation, setPickupLocation] = useState('');
  const [dropoffLocation, setDropoffLocation] = useState('');
  const [vehicleKeyword, setVehicleKeyword] = useState('');
  const [pickupDateInputType, setPickupDateInputType] = useState('text');
  const [dropoffDateInputType, setDropoffDateInputType] = useState('text');
  const [activeLocationPicker, setActiveLocationPicker] = useState(null);
  const [locationPickerQuery, setLocationPickerQuery] = useState('');
  const [locationPickerMapQuery, setLocationPickerMapQuery] = useState('');
  const [selectedLocationSuggestion, setSelectedLocationSuggestion] = useState(null);
  const [googleLocationSuggestions, setGoogleLocationSuggestions] = useState([]);
  const [googleLocationSuggestionState, setGoogleLocationSuggestionState] = useState('idle');
  const [fieldTouched, setFieldTouched] = useState({});
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [showSectionTabs, setShowSectionTabs] = useState(false);
  const [activeSection, setActiveSection] = useState(sectionTabs[0].id);
  const [activeFleetSlide, setActiveFleetSlide] = useState(0);

  const activeFeaturedFleetSlide = featuredFleetSlides[activeFleetSlide];
  const filteredLocationOptions = useMemo(
    () => getFilteredLocationOptions(locationPickerQuery),
    [locationPickerQuery]
  );
  const normalizedLocationQuery = normalizeBookingText(locationPickerQuery);
  const hasExactLocationSuggestion = filteredLocationOptions.some(
    (location) => normalizeBookingText(location) === normalizedLocationQuery
  );
  const hasGoogleLocationSuggestions = googleLocationSuggestions.length > 0;
  const showLocalLocationSuggestions = !hasGoogleLocationSuggestions && googleLocationSuggestionState !== 'loading';
  const showTypedLocationSearch = Boolean(
    locationPickerQuery.trim() &&
    !hasExactLocationSuggestion &&
    !hasGoogleLocationSuggestions &&
    googleLocationSuggestionState !== 'loading'
  );
  const vehicleKeywordMatches = useMemo(() => {
    const keyword = vehicleKeyword.trim();
    if (!keyword) return true;
    return (products || []).some((product) => productMatchesKeyword(product, keyword));
  }, [products, vehicleKeyword]);

  const fieldMessages = useMemo(() => {
    const messages = {};
    if (pickupLocation && pickupLocation.trim().length < 3) {
      messages.pickupLocation = 'Enter at least 3 characters or choose the exact pickup point on the map.';
    }
    if (dropoffLocation && dropoffLocation.trim().length < 3) {
      messages.dropoffLocation = 'Enter at least 3 characters or choose the exact drop-off point on the map.';
    }
    if (pickupDate && pickupDate < tomorrow) {
      messages.pickupDate = 'Pickup date must be tomorrow or later.';
    }
    if (pickupDate && dropoffDate && new Date(dropoffDate) <= new Date(pickupDate)) {
      messages.dropoffDate = 'Drop-off date must be after the pickup date.';
    }
    if (pickupTime && !isValidTimeValue(pickupTime)) {
      messages.pickupTime = 'Select a valid pickup time.';
    }
    if (dropoffTime && !isValidTimeValue(dropoffTime)) {
      messages.dropoffTime = 'Select a valid drop-off time.';
    }
    if (vehicleKeyword.trim() && !vehicleKeywordMatches) {
      messages.vehicleKeyword = 'No matching vehicle is currently listed. Browse all vehicles or choose a name from the database fleet.';
    }
    return messages;
  }, [dropoffDate, dropoffLocation, dropoffTime, pickupDate, pickupLocation, pickupTime, tomorrow, vehicleKeyword, vehicleKeywordMatches]);

  const getFieldMessage = (field) => {
    if (!fieldMessages[field]) return '';
    return field === 'vehicleKeyword' || fieldTouched[field] ? fieldMessages[field] : '';
  };

  const fleetCards = useMemo(() => {
    return buildHomeFleetCards(categories, products);
  }, [categories, products]);

  const dropoffMinDate = useMemo(() => {
    if (!pickupDate) return tomorrow;
    const dayAfterPickup = new Date(pickupDate);
    dayAfterPickup.setDate(dayAfterPickup.getDate() + 1);
    return formatDate(dayAfterPickup);
  }, [pickupDate, tomorrow]);

  const getFixedOffset = useCallback(() => {
    const navbarHeight = document.querySelector('.site-navbar')?.getBoundingClientRect().height || 88;
    const tabsHeight = document.querySelector('.drx-section-tabs')?.getBoundingClientRect().height || 70;
    return navbarHeight + tabsHeight + 18;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollIndicator(window.scrollY <= window.innerHeight * 0.22);
      const navbarHeight = document.querySelector('.site-navbar')?.getBoundingClientRect().height || 88;
      const pageBody = document.querySelector('.drx-page-body');
      setShowSectionTabs(Boolean(pageBody && pageBody.getBoundingClientRect().top <= navbarHeight + 2));
      const activationTop = getFixedOffset();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const activationLine = Math.max(
        activationTop + 26,
        Math.min(viewportHeight - 96, activationTop + Math.max(96, viewportHeight * 0.24))
      );
      const sectionCandidates = sectionTabs.map((tab) => {
        const element = document.getElementById(tab.id);
        if (!element) return null;
        const rect = element.getBoundingClientRect();
        const visibleTop = Math.max(rect.top, activationTop);
        const visibleBottom = Math.min(rect.bottom, viewportHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const candidateScore = visibleHeight - Math.abs(rect.top - activationLine);
        return {
          ...tab,
          candidateScore,
          rectBottom: rect.bottom,
          rectTop: rect.top,
        };
      }).filter(Boolean);
      const currentSection = [...sectionCandidates].reverse().find(({ rectBottom, rectTop }) => (
        rectTop <= activationLine && rectBottom >= activationTop
      )) || sectionCandidates.reduce((best, candidate) => (
        !best || candidate.candidateScore > best.candidateScore ? candidate : best
      ), null);
      setActiveSection(currentSection?.id || sectionTabs[0].id);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [getFixedOffset]);

  useEffect(() => {
    const query = locationPickerQuery.trim();
    if (!activeLocationPicker || query.length < 2 || !canUseGooglePlacesAutocomplete()) {
      setGoogleLocationSuggestions([]);
      setGoogleLocationSuggestionState(query.length >= 2 ? 'fallback' : 'idle');
      return undefined;
    }

    let isStale = false;
    const requestId = googlePlaceRequestIdRef.current + 1;
    googlePlaceRequestIdRef.current = requestId;
    setGoogleLocationSuggestionState('loading');

    getGooglePlacePredictions(query, googlePlaceSessionTokenRef)
      .then((suggestions) => {
        if (isStale || googlePlaceRequestIdRef.current !== requestId) return;
        setGoogleLocationSuggestions(suggestions);
        setGoogleLocationSuggestionState(suggestions.length ? 'ready' : 'empty');
      })
      .catch(() => {
        if (isStale || googlePlaceRequestIdRef.current !== requestId) return;
        setGoogleLocationSuggestions([]);
        setGoogleLocationSuggestionState('fallback');
      });

    return () => {
      isStale = true;
    };
  }, [activeLocationPicker, locationPickerQuery]);

  const validateDropoffAfterPickup = (nextPickupDate, nextDropoffDate) => {
    if (!nextPickupDate || !nextDropoffDate) return;
    const dayAfterPickup = new Date(nextPickupDate);
    dayAfterPickup.setDate(dayAfterPickup.getDate() + 1);
    if (new Date(nextDropoffDate) < dayAfterPickup) {
      setDropoffDate('');
      setDropoffDateInputType('text');
    }
  };

  const handlePickupDateChange = (event) => {
    const value = event.target.value;
    setPickupDate(value);
    validateDropoffAfterPickup(value, dropoffDate);
  };

  const handleDropoffDateChange = (event) => {
    const value = event.target.value;
    setDropoffDate(value);
    validateDropoffAfterPickup(pickupDate, value);
  };

  const markFieldTouched = (field) => {
    setFieldTouched((current) => ({ ...current, [field]: true }));
  };

  const handleDateFocus = (field) => {
    if (field === 'pickup') setPickupDateInputType('date');
    if (field === 'dropoff') setDropoffDateInputType('date');
  };

  const handleDateBlur = (field) => {
    if (field === 'pickup' && !pickupDate) setPickupDateInputType('text');
    if (field === 'dropoff' && !dropoffDate) setDropoffDateInputType('text');
  };

  const openDatePicker = (field) => {
    const input = field === 'pickup' ? pickupDateRef.current : dropoffDateRef.current;
    if (field === 'pickup') setPickupDateInputType('date');
    if (field === 'dropoff') setDropoffDateInputType('date');

    window.requestAnimationFrame(() => {
      input?.focus();
      if (typeof input?.showPicker === 'function') {
        input.showPicker();
      }
    });
  };

  const openTimePicker = (field) => {
    const input = field === 'pickup' ? pickupTimeRef.current : dropoffTimeRef.current;
    input?.focus();
    if (typeof input?.showPicker === 'function') {
      input.showPicker();
    }
  };

  const updateLocationPickerQuery = (value, options = {}) => {
    setLocationPickerQuery(value);
    setLocationPickerMapQuery(options.mapQuery || value);
    if (!options.keepSelection) {
      setSelectedLocationSuggestion(null);
    }
  };

  const openLocationPicker = (field) => {
    const currentLocation = field === 'pickup' ? pickupLocation : dropoffLocation;
    const nextLocation = currentLocation || locationOptions[0];
    setActiveLocationPicker(field);
    setSelectedLocationSuggestion(currentLocation ? {
      label: currentLocation,
      mapQuery: currentLocation,
      source: 'Saved booking field',
    } : null);
    updateLocationPickerQuery(nextLocation, { keepSelection: Boolean(currentLocation) });
  };

  const closeLocationPicker = () => {
    setActiveLocationPicker(null);
    setGoogleLocationSuggestions([]);
    setGoogleLocationSuggestionState('idle');
    setSelectedLocationSuggestion(null);
    resetGooglePlaceSessionToken(googlePlaceSessionTokenRef);
  };

  const confirmLocationPicker = () => {
    const value = (selectedLocationSuggestion?.label || locationPickerQuery).trim();
    if (!value || !activeLocationPicker) return;
    if (activeLocationPicker === 'pickup') {
      setPickupLocation(value);
      markFieldTouched('pickupLocation');
    } else {
      setDropoffLocation(value);
      markFieldTouched('dropoffLocation');
    }
    resetGooglePlaceSessionToken(googlePlaceSessionTokenRef);
    closeLocationPicker();
  };

  const selectGoogleLocationSuggestion = async (suggestion) => {
    updateLocationPickerQuery(suggestion.description, { keepSelection: true });

    try {
      const details = await suggestion.fetchDetails?.();
      const label = getGooglePlaceLabel(details, suggestion.description);
      const mapQuery = details?.locationQuery || label;
      setLocationPickerQuery(label);
      setLocationPickerMapQuery(mapQuery);
      setSelectedLocationSuggestion({
        label,
        mapQuery,
        source: 'Google Maps result',
      });
    } catch {
      setLocationPickerMapQuery(suggestion.description);
      setSelectedLocationSuggestion({
        label: suggestion.description,
        mapQuery: suggestion.description,
        source: 'Google Maps result',
      });
    } finally {
      resetGooglePlaceSessionToken(googlePlaceSessionTokenRef);
    }
  };

  const selectLocalLocationSuggestion = (location) => {
    updateLocationPickerQuery(location, { keepSelection: true, mapQuery: location });
    setSelectedLocationSuggestion({
      label: location,
      mapQuery: location,
      source: 'Suggested DeRoyalty pickup area',
    });
  };

  const selectTypedLocationSearch = () => {
    const label = locationPickerQuery.trim();
    if (!label) return;
    updateLocationPickerQuery(label, { keepSelection: true, mapQuery: label });
    setSelectedLocationSuggestion({
      label,
      mapQuery: label,
      source: 'Typed Google Maps search',
    });
  };

  const scrollToHomeSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    const targetTop = element.getBoundingClientRect().top + window.scrollY - getFixedOffset();
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
    setActiveSection(sectionId);
  };

  const showPreviousFeaturedVehicle = () => {
    setActiveFleetSlide((current) => (current - 1 + featuredFleetSlides.length) % featuredFleetSlides.length);
  };

  const showNextFeaturedVehicle = () => {
    setActiveFleetSlide((current) => (current + 1) % featuredFleetSlides.length);
  };

  const submitPickupSearch = (event) => {
    event.preventDefault();
    setFieldTouched({
      pickupLocation: true,
      dropoffLocation: true,
      pickupDate: true,
      dropoffDate: true,
      pickupTime: true,
      dropoffTime: true,
      vehicleKeyword: true,
    });
    const params = new URLSearchParams();
    if (pickupLocation) params.set('pickupLocation', pickupLocation);
    if (dropoffLocation) params.set('dropoffLocation', dropoffLocation);
    if (pickupDate) params.set('pickupDate', pickupDate);
    if (dropoffDate) params.set('dropoffDate', dropoffDate);
    if (pickupTime) params.set('pickupTime', pickupTime);
    if (dropoffTime) params.set('dropoffTime', dropoffTime);
    if (vehicleKeyword.trim()) {
      if (vehicleKeywordMatches) {
        params.set('q', vehicleKeyword.trim());
      } else {
        params.set('vehicleIntent', vehicleKeyword.trim());
      }
    }
    const queryString = params.toString();
    navigate(queryString ? `/booking/review?${queryString}` : '/booking/review');
  };

  const renderLocationOptions = () => (
    <>
      {googleLocationSuggestions.map((suggestion) => (
        <button
          key={suggestion.id}
          type="button"
          className={`drx-location-suggestion drx-location-suggestion-google ${
            normalizeBookingText(selectedLocationSuggestion?.label) === normalizeBookingText(suggestion.description) ? 'is-selected' : ''
          }`}
          aria-pressed={normalizeBookingText(selectedLocationSuggestion?.label) === normalizeBookingText(suggestion.description)}
          onClick={() => selectGoogleLocationSuggestion(suggestion)}
        >
          <i className="bi bi-geo-alt-fill" aria-hidden="true" />
          <span>{suggestion.mainText || suggestion.description}</span>
          <small>{suggestion.secondaryText || 'Google Maps result'}</small>
        </button>
      ))}
      {showLocalLocationSuggestions && filteredLocationOptions.map((location) => (
        <button
          key={location}
          type="button"
          className={`drx-location-suggestion ${
            normalizeBookingText(selectedLocationSuggestion?.label) === normalizeBookingText(location) ? 'is-selected' : ''
          }`}
          aria-pressed={normalizeBookingText(selectedLocationSuggestion?.label) === normalizeBookingText(location)}
          onClick={() => selectLocalLocationSuggestion(location)}
        >
          <span>{location}</span>
          <small>{normalizeBookingText(selectedLocationSuggestion?.label) === normalizeBookingText(location) ? 'Selected location' : 'Suggested location'}</small>
        </button>
      ))}
      {googleLocationSuggestionState === 'loading' && (
        <p className="drx-location-empty">Searching Google Maps locations...</p>
      )}
      {showTypedLocationSearch && (
        <button
          type="button"
          className="drx-location-suggestion drx-location-suggestion-search"
          aria-pressed={normalizeBookingText(selectedLocationSuggestion?.label) === normalizeBookingText(locationPickerQuery)}
          onClick={selectTypedLocationSearch}
        >
          <i className="bi bi-search" aria-hidden="true" />
          <span>{`Search Google Maps for "${locationPickerQuery.trim()}"`}</span>
          <small>Use this typed address in the map preview</small>
        </button>
      )}
      {!filteredLocationOptions.length && !hasGoogleLocationSuggestions && !showTypedLocationSearch && googleLocationSuggestionState !== 'loading' && (
        <p className="drx-location-empty">Start typing a pickup point, suburb, airport, hotel, or address.</p>
      )}
    </>
  );

  const activeLocationLabel = activeLocationPicker === 'pickup' ? 'pickup' : 'drop-off';
  const locationMapSrc = `https://www.google.com/maps?q=${encodeURIComponent(locationPickerMapQuery || locationPickerQuery || 'DeRoyalty Car Rental Harare')}&output=embed`;
  const pickupLocationMessage = getFieldMessage('pickupLocation');
  const pickupDateMessage = getFieldMessage('pickupDate');
  const pickupTimeMessage = getFieldMessage('pickupTime');
  const dropoffLocationMessage = getFieldMessage('dropoffLocation');
  const dropoffDateMessage = getFieldMessage('dropoffDate');
  const dropoffTimeMessage = getFieldMessage('dropoffTime');
  const vehicleKeywordMessage = getFieldMessage('vehicleKeyword');
  const locationPickerModal = activeLocationPicker ? (
    <div className="drx-location-modal-backdrop" role="presentation" onClick={closeLocationPicker}>
      <section
        className="drx-location-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Select ${activeLocationLabel} location on Google Maps`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="drx-location-modal-head">
          <div>
            <p className="drx-eyebrow">Google Maps location picker</p>
            <h3>Select {activeLocationLabel} location</h3>
          </div>
          <button type="button" onClick={closeLocationPicker} aria-label="Close location picker">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <label className="drx-location-search">
          <span>Map search location</span>
          <input
            value={locationPickerQuery}
            onChange={(event) => updateLocationPickerQuery(event.target.value)}
            type="search"
            placeholder={`Search ${activeLocationLabel} address`}
            autoFocus
          />
        </label>

        <div className="drx-location-suggestions" aria-label="Location suggestions">
          {renderLocationOptions()}
        </div>

        {selectedLocationSuggestion && (
          <div className="drx-location-selected" role="status" aria-live="polite">
            <i className="bi bi-check-circle-fill" aria-hidden="true" />
            <div>
              <span>Selected location</span>
              <strong>{selectedLocationSuggestion.label}</strong>
              <small>{selectedLocationSuggestion.source}</small>
            </div>
          </div>
        )}

        <iframe
          title={`Google Maps ${activeLocationLabel} preview`}
          src={locationMapSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />

        <div className="drx-location-modal-actions">
          <button type="button" className="drx-btn drx-btn-outline" onClick={closeLocationPicker}>
            Cancel
          </button>
          <button
            type="button"
            className="drx-btn drx-btn-gold"
            onClick={confirmLocationPicker}
            disabled={!locationPickerQuery.trim()}
          >
            Use This {activeLocationPicker === 'pickup' ? 'Pickup' : 'Drop-off'} Location
          </button>
        </div>
      </section>
    </div>
  ) : null;

  return (
    <div className="home-page drx-home" ref={rootRef}>
      <section
        className="drx-hero"
        style={{ backgroundImage: `url(${heroImage})` }}
        aria-label="DeRoyalty Car Rental homepage hero"
      >
        <div className="drx-hero-grid">
          <div className="drx-hero-copy" data-anime-enter>
            <div className="drx-hero-text-wrap">
              <p>
                DeRoyalty Car Rental delivers a seamless luxury experience with a premium fleet,
                reliable service and memorable journeys across every destination.
              </p>
            </div>
            <div className="drx-hero-actions">
              <Link to={heroBookHref} className="drx-btn drx-btn-gold">
                Book Now <i className="bi bi-arrow-right" aria-hidden="true" />
              </Link>
              <Link to="/vehicles#complete-fleet" className="drx-btn drx-btn-gold">
                Browse Fleet <i className="bi bi-arrow-right" aria-hidden="true" />
              </Link>
              <Link to="/quote" className="drx-btn drx-btn-outline">Get a Quote</Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          className={`drx-scroll-cue ${showScrollIndicator ? 'visible' : 'hidden'}`}
          onClick={() => scrollToHomeSection('home-fleet')}
          aria-label="Scroll to Our Fleet section"
        >
          <i className="bi bi-chevron-double-down" aria-hidden="true" />
        </button>
      </section>

      <section className="drx-booking-bar" aria-label="Book your vehicle">
        <div className="drx-booking-bar-inner">
          <div className="drx-booking-bar-head">
            <div>
              <p className="drx-eyebrow">Book your journey</p>
              <h2>Find a Vehicle</h2>
              <span>Choose your perfect car and pick it up with ease.</span>
            </div>
            <Link to={userRole ? '/cart' : '/auth?mode=login&next=/cart'} className="drx-manage-link">
              Manage Booking <i className="bi bi-calendar2-check" aria-hidden="true" />
            </Link>
          </div>

          <form className="drx-booking-form-strip" onSubmit={submitPickupSearch}>
            <div className="drx-booking-strip-fields">
              <label className="drx-field">
                <span>Pickup Location</span>
                <div className="drx-input-wrap">
                  <button
                    type="button"
                    className="drx-field-action-button"
                    onClick={() => openLocationPicker('pickup')}
                    aria-label="Open pickup location map"
                  >
                    <i className="bi bi-geo-alt-fill" aria-hidden="true" />
                  </button>
                  <input
                    value={pickupLocation}
                    onChange={(event) => setPickupLocation(event.target.value)}
                    onBlur={() => markFieldTouched('pickupLocation')}
                    type="text"
                    placeholder="Select pickup location"
                    aria-invalid={Boolean(pickupLocationMessage)}
                    aria-describedby={pickupLocationMessage ? 'pickup-location-feedback' : undefined}
                  />
                </div>
                {pickupLocationMessage && (
                  <small id="pickup-location-feedback" className="drx-field-feedback" role="alert">
                    {pickupLocationMessage}
                  </small>
                )}
              </label>

              <label className="drx-field">
                <span>Pick-up Date</span>
                <div className="drx-input-wrap">
                  <button
                    type="button"
                    className="drx-date-picker-button drx-field-action-button"
                    onClick={() => openDatePicker('pickup')}
                    aria-label="Open pick-up date picker"
                  >
                    <i className="bi bi-calendar2-week" aria-hidden="true" />
                  </button>
                  <input
                    ref={pickupDateRef}
                    value={pickupDate}
                    min={tomorrow}
                    onChange={handlePickupDateChange}
                    onFocus={() => handleDateFocus('pickup')}
                    onBlur={() => { handleDateBlur('pickup'); markFieldTouched('pickupDate'); }}
                    type={pickupDateInputType}
                    placeholder="Select date"
                    aria-invalid={Boolean(pickupDateMessage)}
                    aria-describedby={pickupDateMessage ? 'pickup-date-feedback' : undefined}
                  />
                </div>
                {pickupDateMessage && (
                  <small id="pickup-date-feedback" className="drx-field-feedback" role="alert">
                    {pickupDateMessage}
                  </small>
                )}
              </label>

              <label className="drx-field">
                <span>Pick-up Time</span>
                <div className="drx-input-wrap">
                  <button
                    type="button"
                    className="drx-field-action-button drx-time-picker-button"
                    onClick={() => openTimePicker('pickup')}
                    aria-label="Open pick-up time picker"
                  >
                    <i className="bi bi-clock" aria-hidden="true" />
                  </button>
                  <input
                    ref={pickupTimeRef}
                    value={pickupTime}
                    onChange={(event) => setPickupTime(event.target.value)}
                    onBlur={() => markFieldTouched('pickupTime')}
                    type="time"
                    aria-invalid={Boolean(pickupTimeMessage)}
                    aria-describedby={pickupTimeMessage ? 'pickup-time-feedback' : undefined}
                  />
                </div>
                {pickupTimeMessage && (
                  <small id="pickup-time-feedback" className="drx-field-feedback" role="alert">
                    {pickupTimeMessage}
                  </small>
                )}
              </label>

              <label className="drx-field">
                <span>Drop-off Location</span>
                <div className="drx-input-wrap">
                  <button
                    type="button"
                    className="drx-field-action-button"
                    onClick={() => openLocationPicker('dropoff')}
                    aria-label="Open drop-off location map"
                  >
                    <i className="bi bi-geo-alt" aria-hidden="true" />
                  </button>
                  <input
                    value={dropoffLocation}
                    onChange={(event) => setDropoffLocation(event.target.value)}
                    onBlur={() => markFieldTouched('dropoffLocation')}
                    type="text"
                    placeholder="Select drop-off location"
                    aria-invalid={Boolean(dropoffLocationMessage)}
                    aria-describedby={dropoffLocationMessage ? 'dropoff-location-feedback' : undefined}
                  />
                </div>
                {dropoffLocationMessage && (
                  <small id="dropoff-location-feedback" className="drx-field-feedback" role="alert">
                    {dropoffLocationMessage}
                  </small>
                )}
              </label>

              <label className="drx-field">
                <span>Drop-off Date</span>
                <div className="drx-input-wrap">
                  <button
                    type="button"
                    className="drx-date-picker-button drx-field-action-button"
                    onClick={() => openDatePicker('dropoff')}
                    aria-label="Open drop-off date picker"
                  >
                    <i className="bi bi-calendar2-check" aria-hidden="true" />
                  </button>
                  <input
                    ref={dropoffDateRef}
                    value={dropoffDate}
                    min={dropoffMinDate}
                    onChange={handleDropoffDateChange}
                    onFocus={() => handleDateFocus('dropoff')}
                    onBlur={() => { handleDateBlur('dropoff'); markFieldTouched('dropoffDate'); }}
                    type={dropoffDateInputType}
                    placeholder="Select date"
                    aria-invalid={Boolean(dropoffDateMessage)}
                    aria-describedby={dropoffDateMessage ? 'dropoff-date-feedback' : undefined}
                  />
                </div>
                {dropoffDateMessage && (
                  <small id="dropoff-date-feedback" className="drx-field-feedback" role="alert">
                    {dropoffDateMessage}
                  </small>
                )}
              </label>

              <label className="drx-field">
                <span>Drop-off Time</span>
                <div className="drx-input-wrap">
                  <button
                    type="button"
                    className="drx-field-action-button drx-time-picker-button"
                    onClick={() => openTimePicker('dropoff')}
                    aria-label="Open drop-off time picker"
                  >
                    <i className="bi bi-clock-history" aria-hidden="true" />
                  </button>
                  <input
                    ref={dropoffTimeRef}
                    value={dropoffTime}
                    onChange={(event) => setDropoffTime(event.target.value)}
                    onBlur={() => markFieldTouched('dropoffTime')}
                    type="time"
                    aria-invalid={Boolean(dropoffTimeMessage)}
                    aria-describedby={dropoffTimeMessage ? 'dropoff-time-feedback' : undefined}
                  />
                </div>
                {dropoffTimeMessage && (
                  <small id="dropoff-time-feedback" className="drx-field-feedback" role="alert">
                    {dropoffTimeMessage}
                  </small>
                )}
              </label>

              <label className="drx-field drx-field-keyword">
                <span>Vehicle Type / Name</span>
                <div className="drx-input-wrap">
                  <i className="bi bi-car-front" aria-hidden="true" />
                  <input
                    value={vehicleKeyword}
                    onChange={(event) => setVehicleKeyword(event.target.value)}
                    onBlur={() => markFieldTouched('vehicleKeyword')}
                    type="search"
                    list="homeVehicleOptions"
                    placeholder="e.g. Fortuner, Mercedes, Note"
                    aria-invalid={Boolean(vehicleKeywordMessage)}
                    aria-describedby={vehicleKeywordMessage ? 'vehicle-keyword-feedback' : undefined}
                  />
                </div>
                {vehicleKeywordMessage && (
                  <small id="vehicle-keyword-feedback" className="drx-field-feedback" role="alert">
                    {vehicleKeywordMessage}
                  </small>
                )}
              </label>

              <button type="submit" className="drx-search-button drx-strip-submit">
                Find Your Vehicle <i className="bi bi-arrow-right" aria-hidden="true" />
              </button>

              <datalist id="homeVehicleOptions">
                {(products || []).map((product) => (
                  <option key={product.id} value={product.name} />
                ))}
              </datalist>
            </div>

            <div className="drx-benefit-strip">
              {bookingBenefits.map((benefit) => (
                <span key={benefit.label}>
                  <i className={`bi ${benefit.icon}`} aria-hidden="true" />
                  {benefit.label}
                </span>
              ))}
            </div>
          </form>

          {locationPickerModal ? createPortal(locationPickerModal, document.body) : null}
        </div>
      </section>

      <div className="drx-brand-strip" aria-label="Vehicle brands">
        <span>Drive trusted brands</span>
        <div className="drx-brand-viewport">
          <div className="drx-brand-track">
            {[...brandLogos, ...brandLogos].map((logo, index) => {
              const isDuplicate = index >= brandLogos.length;
              return (
                <figure
                  key={`${logo.alt}-${index}`}
                  className="drx-brand-pill"
                  aria-hidden={isDuplicate ? 'true' : undefined}
                >
                  <img src={logo.src} alt={isDuplicate ? '' : logo.alt} />
                </figure>
              );
            })}
          </div>
        </div>
      </div>

      <div className="drx-trust-strip" aria-label="DeRoyalty trust guarantees">
        {trustBenefits.map((benefit) => (
          <article key={benefit.title}>
            <i className={`bi ${benefit.icon}`} aria-hidden="true" />
            <div>
              <strong>{benefit.title}</strong>
              <span>{benefit.text}</span>
            </div>
          </article>
        ))}
      </div>

      <section className="drx-proof-strip" aria-label="DeRoyalty credibility highlights">
        {trustProofStats.map((item) => (
          <article key={item.label}>
            <i className={`bi ${item.icon}`} aria-hidden="true" />
            <strong>{item.value}</strong>
            <span>{item.label}</span>
          </article>
        ))}
      </section>

      <main className="drx-page-body">
        <nav className={`drx-section-tabs ${showSectionTabs ? 'is-visible' : 'is-hidden'}`} aria-label="Home page sections">
          {sectionTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`drx-section-tab ${activeSection === tab.id ? 'active' : ''}`}
              aria-current={activeSection === tab.id ? 'true' : undefined}
              onClick={() => scrollToHomeSection(tab.id)}
            >
              <span>{tab.number}</span>
              <strong>{tab.title}</strong>
            </button>
          ))}
        </nav>

        <section id="home-fleet" className="drx-section drx-fleet-section" data-anime>
          <div className="drx-section-panel drx-fleet-intro">
            <div className="drx-section-copy">
              <p className="drx-section-kicker">01</p>
              <h2>Our Fleet</h2>
              <p>
                Drive excellence with DeRoyalty Car Rental. Our premium fleet is meticulously maintained,
                insured and ready to deliver comfort, performance and style for every journey.
              </p>
              <div className="drx-feature-row">
                {fleetBenefits.map((benefit) => (
                  <article key={benefit.title} className="drx-feature-chip">
                    <i className={`bi ${benefit.icon}`} aria-hidden="true" />
                    <div>
                      <strong>{benefit.title}</strong>
                      <span>{benefit.text}</span>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <figure
              className="drx-image-panel drx-vehicle-panel"
              role="region"
              aria-label="Featured fleet carousel"
              aria-roledescription="carousel"
            >
              <img src={activeFeaturedFleetSlide.image} alt={activeFeaturedFleetSlide.alt} />
              <button type="button" className="drx-carousel-nav drx-carousel-prev" onClick={showPreviousFeaturedVehicle} aria-label="Previous featured vehicle">
                <i className="bi bi-chevron-left" aria-hidden="true" />
              </button>
              <button type="button" className="drx-carousel-nav drx-carousel-next" onClick={showNextFeaturedVehicle} aria-label="Next featured vehicle">
                <i className="bi bi-chevron-right" aria-hidden="true" />
              </button>
              <figcaption aria-live="polite">
                <span>{activeFeaturedFleetSlide.eyebrow}</span>
                <strong>{activeFeaturedFleetSlide.title}</strong>
              </figcaption>
              <div className="drx-carousel-dots" aria-label="Featured fleet slides">
                {featuredFleetSlides.map((slide, index) => (
                  <button
                    key={slide.title}
                    type="button"
                    aria-label={`Featured fleet slide ${index + 1}`}
                    aria-current={activeFleetSlide === index ? 'true' : undefined}
                    onClick={() => setActiveFleetSlide(index)}
                  />
                ))}
              </div>
            </figure>
          </div>

          <div className="drx-section-panel drx-fleet-browser">
            <div className="drx-subsection-head">
              <div>
                <h3>Explore Our Fleet</h3>
                <p>Choose the perfect vehicle for your journey.</p>
              </div>
              <Link to="/vehicles#complete-fleet" className="drx-btn drx-btn-outline">View Our Fleet</Link>
            </div>

            <div className="drx-fleet-grid">
              {fleetCards.map((card) => (
                <Link key={card.id} to={card.type === 'category' ? `/category/show/${card.id}` : '/vehicles'} className="drx-fleet-card">
                  <div className="drx-fleet-card-image">
                    <img src={card.image} alt={card.imageAlt || card.title} />
                    <span>{card.badge}</span>
                  </div>
                  <div className="drx-fleet-card-body">
                    <h4>{card.title}</h4>
                    <p>{card.description}</p>
                    <div className="drx-card-tags">
                      {(card.details || []).slice(0, 3).map((detail) => (
                        <small key={detail}>{detail}</small>
                      ))}
                    </div>
                    <div className="drx-fleet-card-footer">
                      <em>View Range</em>
                      {formatHomeFleetDailyRate(card.price) && (
                        <strong
                          className="drx-fleet-daily-rate"
                          aria-label={`Daily lease rate for ${card.imageAlt || card.title}: ${formatHomeFleetDailyRate(card.price)}`}
                        >
                          {formatHomeFleetDailyRate(card.price)}
                        </strong>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="drx-section drx-why-section" data-anime aria-labelledby="why-deroyalty-title">
          <div className="drx-section-panel drx-why-grid">
            <div className="drx-section-copy">
              <p className="drx-section-kicker">Royal difference</p>
              <h2 id="why-deroyalty-title">Why DeRoyalty</h2>
              <p>
                DeRoyalty combines premium fleet standards, verified customer workflows and a hospitality-led team
                so every booking feels clear before pickup and supported until return.
              </p>
              <div className="drx-why-actions">
                <Link to="/vehicles#complete-fleet" className="drx-btn drx-btn-gold">Explore Fleet</Link>
                <Link to="/quote" className="drx-btn drx-btn-outline">Request a Quote</Link>
              </div>
            </div>

            <div className="drx-why-cards">
              {whyDeroyaltyCards.map((item) => (
                <article key={item.title} className="drx-mini-card">
                  <i className={`bi ${item.icon}`} aria-hidden="true" />
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>

            <div className="drx-testimonial-grid">
              {clientTestimonials.map((item) => (
                <figure key={item.quote} className="drx-testimonial-card">
                  <blockquote>{item.quote}</blockquote>
                  <figcaption>{item.name}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="home-values" className="drx-section drx-values-section" data-anime>
          <div className="drx-section-panel drx-values-grid">
            <div className="drx-section-copy drx-values-copy">
              <div className="drx-values-heading">
                <p className="drx-section-kicker">02</p>
                <h2>Our Values</h2>
              </div>
              <div className="drx-values-copy-body">
                <p>
                  We always place the interests of our customers first. We always conduct business with implementation of high standards, trust, honesty, professionalism and ethical behaviour.
                </p>
                <div className="drx-stacked-features">
                  {valueHighlights.map((item) => (
                    <article key={item.title} className="drx-feature-chip">
                      <i className={`bi ${item.icon}`} aria-hidden="true" />
                      <div>
                        <strong>{item.title}</strong>
                        <span>{item.text}</span>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            <article className="drx-quote-card">
              <p className="drx-quote-mark" aria-hidden="true">“</p>
              <p>
                Talent is the No. 1 priority for a CEO. You think it's about vision and strategy, but you have to get the right people first. Trustworthiness, honesty and reliability are the keystone values of DeRoyalty Car Rental. We are entirely committed to creating fully-cusomized service at the right price, whether it is for an individual or a company's needs. It's as simple as that - we are the brand you can trust.
              </p>
              <footer>
                <strong>BRIAN LANGSONI</strong>
                <span>CEO, DeRoyalty Car Rental</span>
              </footer>
            </article>

            <figure className="drx-image-panel drx-values-image">
              <img src={ceoImage} alt="Brian Langsoni, CEO DeRoyalty Car Rental" />
            </figure>
          </div>
        </section>

        <section id="home-vision" className="drx-section drx-vision-section" data-anime>
          <div className="drx-section-panel drx-vision-grid">
            <div className="drx-section-copy">
              <p className="drx-section-kicker">03</p>
              <h2>Our Vision</h2>
              <p>
                Our Vision is to become one of the leading brands in the rental industry of Africa and to build the wide network of branches in the World.
              </p>
            </div>
            <figure className="drx-image-panel drx-wide-image drx-vision-image">
              <img src={visionImage} alt="DeRoyalty vision" />
              <span className="drx-vision-orbit drx-vision-orbit-one" aria-hidden="true" />
              <span className="drx-vision-orbit drx-vision-orbit-two" aria-hidden="true" />
              <span className="drx-vision-orbit drx-vision-orbit-three" aria-hidden="true" />
              <span className="drx-vision-sphere-glow" aria-hidden="true" />
            </figure>
          </div>
        </section>

        <section id="home-know-how" className="drx-section drx-knowhow-section" data-anime>
          <div className="drx-section-panel drx-knowhow-grid">
            <figure className="drx-image-panel drx-knowhow-image">
              <img src={knowHowImage} alt="DeRoyalty know-how" />
            </figure>

            <div className="drx-section-copy">
              <p className="drx-section-kicker">04</p>
              <h2>Our Know-How</h2>
              <p>
                DeRoyalty Car Rental is driven by a team of car rental specialists originally from Zimbabwe. Across 3 years operating, Brasco Communications has worked with globally well known companies across Europe, North America and Africa.
              </p>
            </div>

            <div className="drx-knowhow-cards">
              {knowHowCards.map((card) => (
                <article key={card.title} className="drx-mini-card">
                  <i className={`bi ${card.icon}`} aria-hidden="true" />
                  <h3>{card.title}</h3>
                  <h4>{card.subtitle}</h4>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="home-locations" className="drx-section drx-locations-section" data-anime>
          <div className="drx-section-panel drx-locations-grid">
            <div className="drx-section-copy">
              <p className="drx-section-kicker">05</p>
              <h2>Our Locations</h2>
              <p>
                We are a premium car rental group in Africa, Australia and the Middle East. Wherever your journey begins,
                there will always be a convenient DeRoyalty Car Rental branch nearby.
              </p>
            </div>

            <div className="drx-map-panel">
              <div className="drx-map-stage">
                <img src={mapMain} alt="DeRoyalty branch locations map" />
                <svg className="drx-map-svg-overlay" viewBox="0 0 1440 935" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                    {/* Route: Harare to Bulawayo */}
                    <path
                      className="drx-map-route"
                      data-anime-draw
                      d="M349,612 C345,628 329,648 311,661"
                      fill="none"
                      stroke="rgba(198, 168, 94, 0.7)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    {/* Route: Bulawayo to Johannesburg */}
                    <path
                      className="drx-map-route"
                      data-anime-draw
                      d="M311,661 C311,700 306,733 300,758"
                      fill="none"
                      stroke="rgba(198, 168, 94, 0.7)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    {/* Route: Harare to Luanda */}
                    <path
                      className="drx-map-route"
                      data-anime-draw
                      d="M349,612 C293,585 210,535 72,478"
                      fill="none"
                      stroke="rgba(198, 168, 94, 0.7)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    {/* Route: Harare to London */}
                    <path
                      className="drx-map-route"
                      data-anime-draw
                      d="M349,612 C410,500 480,346 561,243"
                      fill="none"
                      stroke="rgba(198, 168, 94, 0.7)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    {/* Route: Harare to Dubai */}
                    <path
                      className="drx-map-route"
                      data-anime-draw
                      d="M349,612 C515,534 748,454 914,398"
                      fill="none"
                      stroke="rgba(198, 168, 94, 0.7)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    {/* Route: Dubai to Melbourne */}
                    <path
                      className="drx-map-route"
                      data-anime-draw
                      d="M914,398 C1036,482 1194,640 1266,793"
                      fill="none"
                      stroke="rgba(198, 168, 94, 0.7)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    {/* Location nodes */}
                    <circle cx="349" cy="612" r="8" fill="#D8BF7A" opacity="0.9" className="drx-map-node" />
                    <circle cx="311" cy="661" r="7" fill="#D8BF7A" opacity="0.8" className="drx-map-node" />
                    <circle cx="72" cy="478" r="7" fill="#D8BF7A" opacity="0.8" className="drx-map-node" />
                    <circle cx="300" cy="758" r="7" fill="#D8BF7A" opacity="0.8" className="drx-map-node" />
                    <circle cx="561" cy="243" r="7" fill="#D8BF7A" opacity="0.8" className="drx-map-node" />
                    <circle cx="914" cy="398" r="7" fill="#D8BF7A" opacity="0.8" className="drx-map-node" />
                    <circle cx="1266" cy="793" r="7" fill="#D8BF7A" opacity="0.8" className="drx-map-node" />
                </svg>
              </div>
              <div className="drx-region-cards">
                {locationRegions.map((region) => (
                  <article key={region.label}>
                    <strong>{region.label}</strong>
                    <span>{region.text}</span>
                  </article>
                ))}
              </div>
            </div>

            <div className="drx-location-stats">
              {worldStats.map((stat) => (
                <article key={stat.label} className="drx-stat-card">
                  <i className={`bi ${stat.icon}`} aria-hidden="true" />
                  <div>
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                    <p>{stat.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="drx-cta-bar">
            <figure aria-hidden="true"><img src={carsBlack} alt="" /></figure>
            <div>
              <h3>Let’s <span>Drive</span> Your Next Journey</h3>
              <p>Whether for business or leisure, our team is ready to provide you with the perfect car and unmatched service.</p>
            </div>
            <Link to="/contact" className="drx-btn drx-btn-gold">Contact Us <i className="bi bi-arrow-right" aria-hidden="true" /></Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default HomeView;
